import React, { useState, useEffect } from 'react';
import { LicenseDeleteImpact } from '../api/Users';
import { formatCalendarDate } from '../utils/timezone';

interface DeleteLicenseModalProps {
  impact: LicenseDeleteImpact | null;
  onCancel: () => void;
  onConfirm: (password: string) => Promise<void>;
  loading: boolean;
  stateName: string;
}

const DeleteLicenseModal: React.FC<DeleteLicenseModalProps> = ({
  impact,
  onCancel,
  onConfirm,
  loading,
  stateName
}) => {
  const [step, setStep] = useState<'warning' | 'password'>('warning');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    setStep('warning');
    setPassword('');
    setShowPassword(false);
    setError('');
  }, [impact]);

  const handleContinue = () => {
    setStep('password');
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    setError('');
    try {
      await onConfirm(password);
      // Success - modal will be closed by parent
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to delete license';
      setError(message);
    }
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return 'Not set';
    return formatCalendarDate(date);
  };

  if (!impact) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title">
              {step === 'warning' ? (
                <>⚠️ Delete License for {stateName}?</>
              ) : (
                <>🔒 Confirm Deletion</>
              )}
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onCancel}
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body">
            {step === 'warning' ? (
              <>
                {impact.impactSummary.cyclesCount > 0 ? (
                  /* ── BLOCKED: cycles must be deleted first ── */
                  <>
                    <div className="alert alert-danger mb-3">
                      <strong>Action Required Before Deleting This License</strong>
                    </div>
                    <p className="mb-3">
                      You must <strong>delete all associated cycles</strong> before you can remove this license.
                      Go to <strong>Cycle Management</strong>, delete the cycles listed below, then return here to delete the license.
                    </p>
                    <div className="card border-danger">
                      <div className="card-header bg-danger text-white">
                        <strong>Cycles That Must Be Deleted First ({impact.impactSummary.cyclesCount})</strong>
                      </div>
                      <div className="card-body" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        <ul className="list-group list-group-flush">
                          {impact.impactSummary.cycleDetails.map((cycle) => (
                            <li key={cycle._id} className="list-group-item px-0">
                              <div><strong>{cycle.cyclename}</strong></div>
                              <small className="text-muted">
                                {formatDate(cycle.cyclestart)} – {formatDate(cycle.cycleend)}
                                {' · '}
                                {cycle.professionID}
                              </small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ── CLEAR: no cycles, safe to proceed ── */
                  <>
                    <div className="alert alert-danger mb-3">
                      <strong>Warning:</strong> This action cannot be undone!
                    </div>
                    <p className="mb-3">Deleting this license will permanently remove:</p>
                    <div className="card mb-3">
                      <div className="card-body">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2">
                            <strong>🔗 Class Associations:</strong> {impact.impactSummary.assignmentsCount} category assignment(s) will be removed
                          </li>
                          <li>
                            <strong>✅ Classes:</strong> Your classes will be preserved (only cycle associations removed)
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="alert alert-info mb-0">
                      No cycles are associated with this license. Only the license record will be removed.
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="alert alert-warning mb-3">
                  <strong>Final Step:</strong> Enter your password to confirm deletion
                </div>

                <div className="mb-3">
                  <label className="form-label"><strong>Your Password *</strong></label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoFocus
                      disabled={loading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading) {
                          handleConfirmDelete();
                        }
                      }}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <small className="text-muted">This verifies that you authorize this deletion</small>
                </div>

                {error && (
                  <div className="alert alert-danger mb-0">
                    {error}
                  </div>
                )}

                <div className="alert alert-secondary mb-0">
                  <small>
                    <strong>What will be deleted:</strong>
                    <ul className="mb-0 mt-2">
                      <li>{impact.impactSummary.cyclesCount} cycle(s)</li>
                      <li>{impact.impactSummary.assignmentsCount} class category assignment(s)</li>
                      <li>License record for {stateName}</li>
                    </ul>
                  </small>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              {step === 'warning' && impact.impactSummary.cyclesCount > 0 ? 'Close' : 'Cancel'}
            </button>

            {step === 'warning' ? (
              impact.impactSummary.cyclesCount === 0 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleContinue}
                >
                  Continue to Delete
                </button>
              )
            ) : (
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={handleConfirmDelete}
                disabled={loading || !password}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Deleting...
                  </>
                ) : (
                  'Confirm Delete'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteLicenseModal;
